import { APPX_ORG_API_URL, USER_SERVICE_URL } from '~/modules/config';
import { getPreferredLanguage } from '~/modules/helpers/languageHelper';
import { TpalInfo, UserPerm } from '~/modules/stores/userStore';
import { handleException } from '~/modules/utilities/error_utilities';
import { HttpRequestConfig, http } from '~/modules/utilities/http';
import { setupOrgRequestConfig, setupTMPrivateRequestConfig } from '~/modules/utilities/httpUtilities';
import { UserTokenService } from '../services/userTokenService';

export interface UserContext {
    orgid: string;
    orgstatus: string;
    orgedition: string;
    topCategories: string[];
    mainCategory: string;
    products: string[];
    region: string;
    industry: string;
    userRole: string;
    userLinkedOrgs: string[];
    lastInstalledListings: string[];
}

export interface UserInfo {
    isGuestUser: boolean;
    userId?: string;
    firstName?: string;
    lastName?: string;
    canInstall?: boolean;
    canCheckout?: boolean;
    tbidProfileUrl?: string;
    userPicture?: string;
    savedListings?: string[];
    userToken: string;
    tokenExpirationDate: number;
    userContext: UserContext;
    recentActivity: string[];
    company: string;
    state: string;
    country: string;
    phone: string;
    email: string;
}

export class UserService {
    get endpoint(): string {
        return USER_SERVICE_URL + '/v1';
    }

    async getUser(bypassCache = false): Promise<UserInfo> {
        const url = await this.buildEndpoint();
        const httpConfig: HttpRequestConfig = setupOrgRequestConfig();

        if (bypassCache) {
            httpConfig.params = { 'bypass-cache': true };
        }

        let userInfo: UserInfo;
        try {
            const response = await http.get(url, httpConfig);
            userInfo = response.data;
        } catch (error) {
            handleException(error, 'userService.getUser');
        }
        // @ts-expect-error * Automated Type Error Fix
        return userInfo;
    }

    private async buildEndpoint() {
        const tokenInfo: AnyObject = await UserTokenService.getTokenRegistrationParams();
        return `${this.endpoint}/user?userToken=${tokenInfo.userToken}&expiresTimestamp=${
            tokenInfo.expirationTimestamp
        }&language=${getPreferredLanguage()}`;
    }

    // @ts-expect-error * Automated Type Error Fix
    getOrganizationId(user) {
        const lastLoggedIn = /salesforce:+(00D[a-zA-Z0-9]{15})/.exec(user.profile?.custom_attributes?.LastLoggedInFrom);
        if (lastLoggedIn) {
            return lastLoggedIn[1];
        }
        // if lastLoggedIn isn't a salesforce tapl check
        const tpalinksCount = user.profile?.custom_attributes?.ThirdPartyAccountLinksNumber || 0;
        for (let i = 1; i <= tpalinksCount; i++) {
            const tpalink = `ThirdPartyAccountLink${i}`;
            const tpalinkValue = user.profile.custom_attributes[tpalink];
            const orgId = /salesforce:+(00D[a-zA-Z0-9]{15})/.exec(tpalinkValue);
            if (orgId) {
                return orgId[1];
            }
        }
        return null;
    }

    async getTpalsUserPerms(tpals: TpalInfo[]): Promise<UserPerm[]> {
        const url = APPX_ORG_API_URL + '/userperm';
        const request: HttpRequestConfig = setupTMPrivateRequestConfig();
        request.data = tpals;
        try {
            const { data } = await http.post(url, request);
            return data;
        } catch (error) {
            handleException(error, 'getTpalsUserPerms');
            return [];
        }
    }
}
